import MenuItem from "@mui/material/MenuItem";
import React, { ReactElement, useState } from "react";

import { PresentationType } from "../../../generated/gql/graphql";
import SwitchProduct, {
  SwitchProductProps,
} from "../../../modals/switch-product";
import ReplacePerspectivesIcon from "../../icons/outlines/replace-perspectives";
import ReplaceProductIcon from "../../icons/outlines/replace-product";

import { CanvasElement, CanvasElementProps } from "./canvas-element";
import { CanvasObjectPresentation } from "./canvas-object";
import PresentationSelectorModal, {
  PresentationSelectorModalProps,
} from "./presentation-selector-modal";

export type PackshotElementProps = Omit<
  CanvasElementProps,
  "canvasObject" | "canvasObjectSize" | "imageUrl"
> & {
  packshot: CanvasObjectPresentation;

  objectChanged: (
    data: Partial<
      Pick<
        CanvasObjectPresentation,
        "scale" | "offset" | "presentationId" | "presentation"
      >
    >,
  ) => void;
};

export default function PackshotElement(
  props: PackshotElementProps,
): ReactElement {
  const { packshot, objectChanged, ...canvasElementProps } = props;

  const [productModal, setProductModal] = useState<
    Omit<SwitchProductProps, "headline"> | undefined
  >(undefined);
  const [presentationModal, setPresentationModal] = useState<
    PresentationSelectorModalProps | undefined
  >();

  const handlePresentationSelect = (
    presentation: CanvasObjectPresentation["presentation"],
  ): void => {
    objectChanged({
      presentationId: presentation?.id,
      presentation,
    });

    setPresentationModal(undefined);
  };

  const handleProductChanged = (productId: string): void => {
    setPresentationModal({
      name: packshot.presentation?.name ?? undefined,
      productId,
      onClose: () => setPresentationModal(undefined),
      onSelect: (presentation) => handlePresentationSelect(presentation),
      selectedPresentationId: undefined,
    });
    setProductModal(undefined);
  };

  if (!packshot.presentation) {
    return <></>;
  }

  const { productId } = packshot.presentation;
  const url =
    packshot.presentation.type === PresentationType.Animation
      ? packshot.presentation.thumbnailUrl
      : packshot.presentation.url;

  function onChangePresentation(): void {
    if (productId) {
      setPresentationModal({
        productId,
        selectedPresentationId: packshot.presentationId ?? undefined,
        onClose: () => setPresentationModal(undefined),
        onSelect: (presentation) => handlePresentationSelect(presentation),
      });
    }
  }

  function onChangeProduct(): void {
    if (productId) {
      setProductModal({
        selectedProductId: productId,
        productSelected: (product) => handleProductChanged(product.id),
        onClose: () => setProductModal(undefined),
      });
    }
  }

  const menuItemChangePresentation = (
    <MenuItem
      key="replace-presentation"
      onClick={() => onChangePresentation()}
      className="h-[30px] !justify-center !p-[5px]"
    >
      <ReplacePerspectivesIcon className="!text-[20px]" />
    </MenuItem>
  );
  const menuItemChangeProduct = (
    <MenuItem
      key="replace-product"
      onClick={() => onChangeProduct()}
      className="h-[30px] !justify-center !border-t-[1px] !border-solid !border-slate-300 !p-[5px]"
    >
      <ReplaceProductIcon className="!text-[20px]" />
    </MenuItem>
  );

  let canvasObjectSize;
  if (packshot.presentation.asset) {
    const canvasSize = canvasElementProps.canvasSize;
    const referenceResolutionScale =
      canvasSize.width / packshot.presentation.referenceResolution;
    canvasObjectSize = {
      width: packshot.presentation.asset.width * referenceResolutionScale,
      height: packshot.presentation.asset.height * referenceResolutionScale,
    };
  } else {
    canvasObjectSize = {
      width: 200,
      height: 200,
    };
  }

  return (
    <>
      <CanvasElement
        canvasObject={packshot}
        canvasObjectSize={canvasObjectSize}
        lockAspectRatio={true}
        imageUrl={url}
        objectChanged={objectChanged}
        {...canvasElementProps}
        menuItems={[menuItemChangePresentation, menuItemChangeProduct]}
      />

      {presentationModal && (
        <PresentationSelectorModal {...presentationModal} />
      )}
      {productModal && (
        <SwitchProduct {...productModal} headline="Switch Product" />
      )}
    </>
  );
}
